import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { getPreferredClient } from '@/lib/auth';

interface UseUserPreferenceOptions<T> {
  key: string;
  defaultValue: T;
  userId?: string; // Optional userId to set preference for a specific user
}

export function useUserPreference<T>({ key, defaultValue, userId }: UseUserPreferenceOptions<T>) {
  const [value, setValue] = useState<T>(defaultValue);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuthStore();

  const inputUserId = userId ?? user?.id;

  useEffect(() => {
    const fetchPreference = async () => {
      try {
        // This needs to be avalible for both monitoring and admin APIs

        const response = await getPreferredClient().get(`/api/UserPreferences/${key}`);

        if (response.status === 200) {
          setValue(response.data.preference?.value as T);
        }
      } catch (error) {
        console.error('Error fetching user preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (inputUserId) {
      fetchPreference();
    }
  }, [key, inputUserId]);

  const updatePreference = useCallback(
    async (newValue: T) => {
      // Optimistic update: set value immediately before API call
      setValue(newValue);

      // Fire-and-forget API call - don't block UI on errors
      getPreferredClient()
        .post('/api/UserPreferences', {
          userId: inputUserId,
          key,
          value: newValue,
        })
        .catch((error) => {
          console.error('Error updating user preference:', error);
          // Note: We don't revert the optimistic update on error.
          // The preference may reappear on next session if the API failed.
        });
    },
    [key, inputUserId]
  );

  return {
    value,
    setValue: updatePreference,
    isLoading,
  };
}

/**
 * Set onboarding_required preference for a specific user, when creating new users to enable onboarding for them
 */
export async function setUserOnboardingRequired(userId: string, value: string = 'true'): Promise<void> {
  try {
    await getPreferredClient().post('/api/UserPreferences', {
      userId,
      key: 'onboarding_required',
      value,
    });
  } catch (error) {
    console.error('Error setting onboarding_required preference:', error);
  }
}
